"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CircleCheck } from "lucide-react";
export default function InquiryForm() {
  return <section data-section-id="222208" id="book-your-event" className="py-24 bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4 tracking-wider uppercase">
            Book Your Event
          </h2>
          <p className="text-muted-foreground font-default text-lg">
            Host your next celebration at Sultani. Fill out the details below to start planning.
          </p>
        </div>

        <div className="bg-card p-6 md:p-10 rounded-none shadow-2xl border border-border">
          <FormProvider formKey="event_booking_form_catering" formId="event_booking_form_catering" sectionName="Inquiry Form Section" defaultValues={{
          name: "",
          email: "",
          phone: "",
          company: "",
          inquiry_type: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          inquiry_type: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="Secure Your Date" successMessage="Your request has been received. Our events team will contact you shortly.">
            <InquiryFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <CircleCheck className="size-16 text-primary" />
        <h3 className="text-2xl font-default font-bold text-foreground uppercase tracking-wider">Request Received</h3>
        <p className="text-muted-foreground font-default max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another Request
        </Button>
      </div>;
  }
  const nativeInputClasses = "flex w-full min-w-0 rounded-none border border-border bg-background px-3 py-2 text-base shadow-none transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[1px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium text-foreground uppercase tracking-tight">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your name" className="bg-background rounded-none border-border focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">As it appears on your ID.</FormDescription>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium text-foreground uppercase tracking-tight">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" className="bg-background rounded-none border-border focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">For booking confirmation.</FormDescription>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^\+?[0-9\s\-()]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium text-foreground uppercase tracking-tight">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="972-000-0000" className="bg-background rounded-none border-border focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">Best number to reach you.</FormDescription>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium text-foreground uppercase tracking-tight">Event Date</FormLabel>
              <FormControl>
                <Input type="text" placeholder="MM/DD/YYYY" className="bg-background rounded-none border-border focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">Preferred date for your event.</FormDescription>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an event type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium text-foreground uppercase tracking-tight">Event Type</FormLabel>
            <FormControl>
              <select className={`${nativeInputClasses} h-10`} {...field}>
                <option value="" disabled>Select an option</option>
                <option value="Corporate Dinner">Corporate Dinner</option>
                <option value="Birthday Celebration">Birthday Celebration</option>
                <option value="Wedding Reception">Wedding Reception</option>
                <option value="Other">Other Private Event</option>
              </select>
            </FormControl>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium text-foreground uppercase tracking-tight">Event Details</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about guest count, dietary needs, or special requests..." className={`${nativeInputClasses} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">Help us make your event perfect.</FormDescription>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
          <p className="text-destructive text-sm font-default font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-full h-12 text-base font-default font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all uppercase tracking-widest">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : 'Secure Your Date'}
      </Button>
    </Form>;
}